import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

/**
 * Переиспользуемый BottomNavigation компонент
 * С плавными анимациями переключения вкладок, как в TwinBuy
 *
 * @param {Object} props
 * @param {Array} props.items - Элементы навигации
 * @param {string} props.className - Дополнительные CSS классы
 * @param {string} props.variant - Вариант стиля
 */
const BottomNavigation = ({
  items = [],
  className = "",
  variant = "default",
  ...props
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Дефолтные элементы навигации если не переданы
  const defaultItems = [
    {
      id: "meet",
      label: "Знакомства",
      icon: "/icons/bottom_bar_button_1.svg",
      path: "/meet",
    },
    {
      id: "chats",
      label: "Чаты",
      icon: "/icons/bottom_bar_button_2.svg",
      path: "/chats",
    },
    {
      id: "streams",
      label: "Стримы",
      icon: "/icons/bottom_bar_button_3.svg",
      path: "/streams",
    },
    {
      id: "profile",
      label: "Профиль",
      icon: "/icons/bottom_bar_button_4.svg",
      path: "/profile",
    },
  ];

  const navigationItems = items.length > 0 ? items : defaultItems;

  const variants = {
    default: "bg-white border-t border-gray-200",
    transparent: "bg-transparent",
    colored: "bg-[#A1F69E]",
  };

  const navClasses = `
    fixed bottom-0 left-0 right-0
    flex items-center justify-around
    w-full h-20 px-2 py-2
    ${variants[variant] || variants.default}
    z-50
    ${className}
  `
    .trim()
    .replace(/\s+/g, " ");

  const handleItemClick = (item) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.path) {
      navigate(item.path);
    }
  };

  // Варианты анимации для кнопок навигации
  const buttonVariants = {
    inactive: {
      scale: 1,
      y: 0,
    },
    active: {
      scale: 1.1,
      y: -2,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 17,
      },
    },
    tap: {
      scale: 0.95,
      transition: {
        duration: 0.1,
      },
    },
  };

  // Варианты анимации для иконок
  const iconVariants = {
    inactive: {
      opacity: 0.7,
      scale: 1,
    },
    active: {
      opacity: 1,
      scale: 1.15,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 17,
      },
    },
  };

  return (
    <nav className={navClasses} {...props}>
      {navigationItems.map((item) => {
        const isActive =
          location.pathname === item.path ||
          (item.activePaths && item.activePaths.includes(location.pathname));

        return (
          <motion.button
            key={item.id}
            onClick={() => handleItemClick(item)}
            variants={buttonVariants}
            initial="inactive"
            animate={isActive ? "active" : "inactive"}
            whileTap="tap"
            className={`
              flex flex-col items-center justify-center
              min-w-0 flex-1 py-1 px-1 relative
              transition-colors duration-300 ease-out
              ${isActive ? "text-[#A1F69E]" : "text-gray-500"}
            `}
          >
            {item.icon && (
              <motion.img
                src={item.icon}
                alt={item.label}
                variants={iconVariants}
                initial="inactive"
                animate={isActive ? "active" : "inactive"}
                className="w-6 h-6 mb-1"
              />
            )}
            {item.customIcon && item.customIcon}
            <motion.span
              className="text-xs font-medium truncate"
              initial={{ opacity: 0.8 }}
              animate={{
                opacity: isActive ? 1 : 0.8,
                fontWeight: isActive ? 600 : 500,
              }}
              transition={{ duration: 0.2 }}
            >
              {item.label}
            </motion.span>
            {item.badge && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 500,
                  damping: 15,
                }}
                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center"
              >
                {item.badge}
              </motion.div>
            )}

            {/* Индикатор активной вкладки */}
            {isActive && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-1/2 w-1 h-1 rounded-full bg-[#A1F69E]"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 500,
                  damping: 30,
                }}
                style={{ x: "-50%" }}
              />
            )}
          </motion.button>
        );
      })}
    </nav>
  );
};

export default BottomNavigation;
